// Campaign filter construction + rendering, shared by CampaignForm (the
// editor) and CampaignDetail (the read-only Overview). Extracted from
// those two files in S6 so the two filter shapes have one home and can
// be unit-tested without mounting React.
//
// Two shapes, discriminated by the list config's source:
//
//   affinity (v1) : {"status": {"field_id": 5681559, "values": [23923498]}}
//   crm      (v2) : {"status": {"field_key": "stage",  "values": ["Invited"]}}
//
// The v2 values are option LABELS. The native options cache the admin UI
// reads is `{label: ordinal}` where the ordinals are placeholders kept
// only to preserve the response schema — the KEYS are the truth (see
// routers/outreach.py::_refresh_options_native, and the server-side
// contract in recipient_enroller._validate_native_filter_alignment).

import type {
  AffinityListConfig,
  CampaignFilter,
  CampaignFilterSlot,
} from '../../types/outreach';

/** A selected filter value: an Affinity option id, or a CRM option label. */
export type FilterValue = number | string;

export type FilterSlotName = 'status' | 'scope';

export type BuildFilterResult =
  | { ok: true; filter: CampaignFilter }
  | { ok: false; error: string };


/** crm-sourced lists build v2 (field_key + labels) filters. */
export function isNativeList(
  list: Pick<AffinityListConfig, 'source_kind'> | null | undefined,
): boolean {
  return list?.source_kind === 'crm';
}

/**
 * Does this list offer the optional second (scope) filter axis? Affinity
 * lists answer with the Affinity field id, native lists with the CRM
 * field key.
 */
export function hasScopeAxis(
  list: AffinityListConfig | null | undefined,
): boolean {
  if (!list) return false;
  return isNativeList(list)
    ? !!list.crm_scope_field_key
    : !!list.scope_field_id;
}

/** Human name for the scope axis ("Campaign" on AGM 2026). */
export function scopeAxisName(
  list: AffinityListConfig | null | undefined,
  fallback = 'Scope',
): string {
  if (!list) return fallback;
  return list.scope_field_name
    || (isNativeList(list) ? list.crm_scope_field_key : null)
    || fallback;
}

/**
 * Where a config's source data lives, for operator-facing copy. Affinity
 * lists keep saying "Affinity" so nothing changes for them today.
 */
export function listSourceLabel(
  list: AffinityListConfig | null | undefined,
): string {
  return isNativeList(list) ? 'the CRM list' : 'Affinity';
}


function buildSlot(
  list: AffinityListConfig,
  slot: FilterSlotName,
  values: FilterValue[],
): { ok: true; slot: CampaignFilterSlot } | { ok: false; error: string } {
  if (isNativeList(list)) {
    const fieldKey = slot === 'status'
      ? list.crm_status_field_key
      : list.crm_scope_field_key;
    if (!fieldKey) {
      return {
        ok: false,
        error:
          `This list is native but has no ${slot} field key configured — `
          + 're-run the audience import before filtering on it.',
      };
    }
    // Labels, never the placeholder ordinals from the options cache.
    return { ok: true, slot: { field_key: fieldKey, values: values.map(String) } };
  }
  const fieldId = slot === 'status' ? list.status_field_id : list.scope_field_id;
  if (!fieldId) {
    return { ok: false, error: `This list has no ${slot} field configured.` };
  }
  return {
    ok: true,
    slot: {
      field_id: fieldId,
      values: values.filter((v): v is number => typeof v === 'number'),
    },
  };
}


/**
 * Build the campaign filter JSON payload for the selected list. Slots
 * with no selected values are omitted entirely (= "include everything"),
 * matching the pre-S6 behavior byte-for-byte on affinity lists.
 */
export function buildCampaignFilter(
  list: AffinityListConfig,
  selection: { status?: FilterValue[]; scope?: FilterValue[] },
): BuildFilterResult {
  const filter: CampaignFilter = {};
  const statusValues = selection.status || [];
  if (statusValues.length > 0) {
    const built = buildSlot(list, 'status', statusValues);
    if (!built.ok) return built;
    filter.status = built.slot;
  }
  const scopeValues = selection.scope || [];
  if (scopeValues.length > 0 && hasScopeAxis(list)) {
    const built = buildSlot(list, 'scope', scopeValues);
    if (!built.ok) return built;
    filter.scope = built.slot;
  }
  return { ok: true, filter };
}


/** Serialize a built filter the way the API expects: null when empty. */
export function filterToJson(filter: CampaignFilter): string | null {
  return Object.keys(filter).length > 0 ? JSON.stringify(filter) : null;
}


/**
 * Parse a stored filter into the multi-select-shaped pair
 * `{status, scope}`. Affinity slots yield option ids (numbers), native
 * slots yield labels (strings) — the picker's value type follows the
 * selected list's source, and a list change resets both arrays, so the
 * two never mix.
 *
 * Legacy shapes (`{Status: "label"}`) are ignored: the operator's option
 * IDs aren't recoverable from labels without the list config's cached
 * options dict, and Edit-mode reset-to-empty is a safer default than
 * silently misrendering.
 */
export function parseFilterJson(
  filterJson?: string | null,
): { status: FilterValue[]; scope: FilterValue[] } {
  const empty = { status: [] as FilterValue[], scope: [] as FilterValue[] };
  if (!filterJson) return empty;
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(filterJson);
  } catch {
    return empty;
  }
  return {
    status: _slotValues(parsed?.status),
    scope: _slotValues(parsed?.scope),
  };
}


function _slotValues(block: unknown): FilterValue[] {
  if (!block || typeof block !== 'object') return [];
  const values = (block as { values?: unknown }).values;
  if (!Array.isArray(values)) return [];
  return values.filter(
    (v): v is FilterValue => typeof v === 'number' || typeof v === 'string',
  );
}


// ---------------------------------------------------------------------------
// Read-only rendering (Overview tab)
// ---------------------------------------------------------------------------

export interface ResolvedFilter {
  status: { values: string[] } | null;
  scope: { fieldName: string; values: string[] } | null;
  rawJson: string | null;
}

/** Build {option_id: label} from a list config's {label: option_id} cache. */
function _invertOptions(
  cache: Record<string, number> | undefined | null,
): Record<number, string> {
  const out: Record<number, string> = {};
  if (!cache) return out;
  for (const [label, id] of Object.entries(cache)) out[id] = label;
  return out;
}

function _resolveValues(
  values: unknown[],
  optionLabels: Record<number, string>,
): string[] {
  return values.map((v) => {
    // v2 values are already labels; v1 values resolve through the cache.
    if (typeof v === 'string') return v;
    if (typeof v === 'number') return optionLabels[v] || `option ${v}`;
    return String(v);
  });
}

/**
 * Parse a stored campaign filter into display labels. Returns nulls when
 * the filter is empty or the list config hasn't loaded; `rawJson` is the
 * fallback for legacy / malformed shapes.
 */
export function resolveFilter(
  filterJson: string | null,
  list: AffinityListConfig | undefined,
): ResolvedFilter {
  if (!filterJson) return { status: null, scope: null, rawJson: null };
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(filterJson);
  } catch {
    return { status: null, scope: null, rawJson: filterJson };
  }
  const statusOpts = _invertOptions(list?.status_dropdown_options);
  const scopeOpts = _invertOptions(list?.scope_dropdown_options);
  const out: ResolvedFilter = { status: null, scope: null, rawJson: null };

  const statusBlock = parsed.status as
    | { field_id?: number; field_key?: string; values?: unknown[] }
    | undefined;
  if (
    (statusBlock?.field_id || statusBlock?.field_key)
    && Array.isArray(statusBlock.values)
  ) {
    out.status = { values: _resolveValues(statusBlock.values, statusOpts) };
  }

  const scopeBlock = parsed.scope as
    | { field_id?: number; field_key?: string; values?: unknown[] }
    | undefined;
  if (
    (scopeBlock?.field_id || scopeBlock?.field_key)
    && Array.isArray(scopeBlock.values)
  ) {
    out.scope = {
      fieldName: scopeAxisName(list, 'scope'),
      values: _resolveValues(scopeBlock.values, scopeOpts),
    };
  }

  // No structured shape we recognized — surface the raw JSON so admins
  // aren't blind. Legacy `{"Status": "<label>"}` shape lands here.
  if (!out.status && !out.scope) {
    out.rawJson = filterJson;
  }
  return out;
}
